import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Save, Palette } from 'lucide-react';

const FONT_OPTIONS = [
  { key: 'system_sans', label: 'System Sans' },
  { key: 'serif_display', label: 'Serif Display' },
  { key: 'condensed_bold', label: 'Condensed' },
];
const BTN_OPTIONS = [
  { key: 'rounded', label: 'Rounded' },
  { key: 'pill', label: 'Pill' },
  { key: 'square', label: 'Square' },
];
const RADIUS_OPTIONS = [
  { key: 'none', label: 'None' },
  { key: 'small', label: 'Small' },
  { key: 'medium', label: 'Medium' },
  { key: 'large', label: 'Large' },
];
const SPACING_OPTIONS = [
  { key: 'compact', label: 'Compact' },
  { key: 'normal', label: 'Normal' },
  { key: 'spacious', label: 'Spacious' },
];

const DEFAULTS = {
  primary_color: '#0f172a', secondary_color: '#f8fafc', accent_color: '#dc2626', neutral_color: '#334155',
  heading_font: 'system_sans', body_font: 'system_sans', button_style: 'rounded', border_radius: 'medium', section_spacing: 'normal',
};

const L = ({ children }) => <Label className="text-gray-300 text-[11px] uppercase tracking-widest">{children}</Label>;
const Pills = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => (
      <button key={o.key} type="button" data-testid={testid ? `${testid}-${o.key}` : undefined} onClick={() => onChange(o.key)}
        className={`px-4 py-2 rounded text-sm border-2 transition-all ${value === o.key ? 'border-red-500 bg-slate-950 text-white' : 'border-slate-800 bg-slate-950/40 text-gray-400 hover:border-slate-700'}`}>{o.label}</button>
    ))}
  </div>
);
const ColorField = ({ label, value, onChange, testid }) => (
  <div className="space-y-1">
    <L>{label}</L>
    <div className="flex items-center gap-2">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-12 h-10 rounded bg-slate-800 border border-slate-700 cursor-pointer" />
      <span className="text-gray-400 text-sm font-mono">{value}</span>
    </div>
  </div>
);

export default function AdminDesign({ settings, onSave, saving }) {
  const [local, setLocal] = useState({ ...DEFAULTS, ...(settings.theme?.design || {}) });
  useEffect(() => { setLocal({ ...DEFAULTS, ...(settings.theme?.design || {}) }); }, [settings.theme?.design]);
  const set = (k) => (v) => setLocal((p) => ({ ...p, [k]: v }));
  const dirty = JSON.stringify(local) !== JSON.stringify({ ...DEFAULTS, ...(settings.theme?.design || {}) });
  const save = () => onSave({ theme: { ...settings.theme, design: local } }, 'Design updated across the site');

  return (
    <div className="space-y-4" data-testid="admin-design">
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2"><Palette className="w-4 h-4" /> Global colors</CardTitle>
          <p className="text-xs text-gray-400">The accent color updates buttons, links, and highlights across the entire website.</p>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <ColorField label="Primary (Navy)" value={local.primary_color} onChange={set('primary_color')} testid="design-primary" />
          <ColorField label="Secondary (White)" value={local.secondary_color} onChange={set('secondary_color')} testid="design-secondary" />
          <ColorField label="Accent" value={local.accent_color} onChange={set('accent_color')} testid="design-accent" />
          <ColorField label="Neutral (Gunmetal)" value={local.neutral_color} onChange={set('neutral_color')} testid="design-neutral" />
        </CardContent>
      </Card>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader><CardTitle className="text-white">Typography</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1"><L>Heading font</L><Pills options={FONT_OPTIONS} value={local.heading_font} onChange={set('heading_font')} testid="design-heading-font" /></div>
          <div className="space-y-1"><L>Body font</L><Pills options={FONT_OPTIONS} value={local.body_font} onChange={set('body_font')} testid="design-body-font" /></div>
        </CardContent>
      </Card>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader><CardTitle className="text-white">Buttons &amp; spacing</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1"><L>Button style</L><Pills options={BTN_OPTIONS} value={local.button_style} onChange={set('button_style')} testid="design-btn-style" /></div>
          <div className="space-y-1"><L>Corner radius</L><Pills options={RADIUS_OPTIONS} value={local.border_radius} onChange={set('border_radius')} testid="design-radius" /></div>
          <div className="space-y-1"><L>Section spacing</L><Pills options={SPACING_OPTIONS} value={local.section_spacing} onChange={set('section_spacing')} testid="design-spacing" /></div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button data-testid="design-save" onClick={save} disabled={saving || !dirty} className="bg-red-600 hover:bg-red-700 text-white">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save design'}
        </Button>
      </div>
    </div>
  );
}
